import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import $ from "jquery";
import "datatables.net";
import "datatables.net-responsive";
import Select, { SingleValue, components } from "react-select";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
    Row,
} from "@tanstack/react-table";
import { ExportAsExcel } from "@siamf/react-export";
import { showSuccess, showError } from "toast/toast";
import DataTable from "components/DataTable";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { InstallationStatusBadge } from "components/Installationstatus";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import { ViewIconButton, EditIconButton, DeleteIconButton, UpdateIconButton } from "components/DatatablesActionIcons";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";

const ORDER_STATUS_OPTIONS = [
    { value: "", label: "Order Status" },
    // { value: "100", label: "Draft" },
    // { value: "200", label: "Finalized" },
    { value: "300", label: "Scheduled" },
    { value: "400", label: "In Progress" },
    { value: "500", label: "Completed" },
    { value: "600", label: "Cancelled" }
];

const PRODUCTION_STATUS_OPTIONS = [
    { value: "", label: "Production Status" },
    { value: "not_started", label: "Not Started" },
    { value: "in_queue", label: "In Queue" },
    { value: "design_approved", label: "Design Approved" },
    { value: "in_production", label: "In Production" },
    { value: "quality_check", label: "Quality Check" },
    { value: "ready_for_pickup", label: "Ready for Pickup" },
    { value: "ready_for_installation", label: "Ready for Installation" }
];

interface ProductionExportRow {
    "Order ID": string;
    "Order Date": string;
    "Product Name": string;
    "Customer": string;
    "Office Name": string;
    "Status": string;
}

interface ProductionTableMeta {
    productionStatusMap: Record<string, string>;
    setProductionStatusMap: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    updateProductionStatus: (installationId: string, productId: string, status: string) => Promise<void>;
}

function fuzzyTextFilter<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
) {
    const raw = row.getValue(columnId);
    const value = (typeof raw === "boolean"
        ? raw
            ? "Active"
            : "Inactive"
        : String(raw)
    ).toLowerCase();

    return value.includes(filterValue.toLowerCase());
}

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    indicatorSeparator: () => ({ display: "none" }),
    control: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
        cursor: s.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),
    singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
    placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
    menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

const rsSelect32CSS = `
/* scope everything under .pss so it won't affect other selects */
.pss .rs__control .rs__indicators,
.pss .rs__control .rs__dropdown-indicator {
  height: 32px !important;
  align-items: center !important;
  display: flex !important;
}

.pss .rs__control { min-height: 32px !important; height: 32px !important; }
.pss .rs__value-container {
  height: 32px !important;
  padding: 0 8px !important;
  display: flex !important;
  align-items: center !important;
}

.pss .rs__single-value,
.pss .rs__placeholder {
  position: static !important;
  transform: none !important;
  top: auto !important;
  margin: 0 !important;
  line-height: 32px !important;
}

.pss .rs__input-container { margin: 0 !important; padding: 0 !important; }
.pss .rs__indicators { height: 32px !important; align-items: center !important; }
.pss .rs__dropdown-indicator { padding: 0 8px !important; height: 32px !important; }
`;

const anchoredSelectStyles = {
    control: (base: any) => ({ ...base, minHeight: 32, height: 32, padding: 0 }),
    valueContainer: (base: any) => ({
        ...base,
        height: 32,
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
    }),
    indicatorsContainer: (base: any) => ({
        ...base,
        height: 32,
        display: "flex",
        alignItems: "center",
    }),
    dropdownIndicator: (base: any) => ({
        ...base,
        padding: "0 8px",
        height: 32,
        display: "flex",
        alignItems: "center",
    }),
    clearIndicator: (base: any) => ({
        ...base,
        height: 32,
        display: "flex",
        alignItems: "center",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    singleValue: (base: any) => ({
        ...base,
        position: "static",
        transform: "none",
        margin: 0,
        lineHeight: "32px",
    }),
    placeholder: (base: any) => ({
        ...base,
        position: "static",
        transform: "none",
        margin: 0,
        lineHeight: "32px",
    }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
};



const columnHelper = createColumnHelper<any>();

const SignageProductionOverview = () => {
    const [products, setProducts] = useState<any[]>([]);
    // const [products, setProducts] = useState<Product[]>([]);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string | null>(null);
    const [toastShown, setToastShown] = useState(false);
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
    const [productionStatusMap, setProductionStatusMap] = useState<Record<string, string>>({});
    const [totalPages, setTotalPages] = useState<number>(0);
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({ pageIndex: 0, pageSize: 10 });
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [orderStatusFilter, setOrderStatusFilter] = useState(ORDER_STATUS_OPTIONS[0]);
    const [productionStatusFilter, setProductionStatusFilter] = useState(PRODUCTION_STATUS_OPTIONS[0]);
    const [appliedFilters, setAppliedFilters] = useState({
        orderStatus: ORDER_STATUS_OPTIONS[0],
        productionStatus: PRODUCTION_STATUS_OPTIONS[0]
    });
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [exportRows, setExportRows] = useState<ProductionExportRow[]>([]);

    // Production status options - outside of return
    const productionStatusOptions = [
        { value: "not_started", label: "Not Started" },
        { value: "in_queue", label: "In Queue" },
        { value: "design_approved", label: "Design Approved" },
        { value: "in_production", label: "In Production" },
        { value: "quality_check", label: "Quality Check" },
        { value: "ready_for_pickup", label: "Ready for Pickup" },
        { value: "ready_for_installation", label: "Ready for Installation" },
    ];

    // Map order IDs to their default selected status option
    const orderProductionStatusMap: Record<string, { value: string; label: string }> = {};
    products.forEach((order) => {
        const firstLineItem = order.line_items?.[0];
        const statusValue = firstLineItem?.production_status ?? "not_started";
        const matched = productionStatusOptions.find((opt) => opt.value === statusValue);
        orderProductionStatusMap[order._id] = matched || productionStatusOptions[0];
    });

    const updateProductionStatus = async (
        installationId: string,
        variantId: string,
        productId: string,
        productionStatus: string
    ) => {
        if (!installationId || !token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/orders/${installationId}/update_production_status`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        product_id: productId,
                        variant_id: variantId,
                        production_status: productionStatus,
                    }),
                }
            );

            const data = await response.json();
            console.log("updateProductionStatus:", data);

            if (!response.ok) {
                showError(data?.error || "Failed to update production status");
                return;
            }

            showSuccess("Production status updated successfully!");
        } catch (err: any) {
            showError(err.message || "Error updating production status");
        }
    };

    const normalizeOrders = (orders: any[]) => {
        // Backend now returns flattened data, just map to expected format
        return orders.map(order => ({
            order_id: order.order_id,
            order_number: order.order_number,
            created_at: order.order_date,
            email: order.email || "---",
            brokerage_firm_name: order.brokerage_firm || "---",
            office: order.office || null,
            office_name: order.office || "---",
            agent_name: order.agent || "---",
            status: order.status || 0,
            product_id: order.product_id,
            variant_id: order.variant_id || order.product_id, // fallback to product_id if variant_id doesn't exist
            product_name: order.product_name,
            production_status: order.production_status || "not_started",
            sku: order.sku,
            quantity: order.quantity,
            payment_status: order.payment_status,
            // For export
            customer_name: order.agent || "---",
        }));
    };



    useEffect(() => {
        if (isSignageTeammember && token) {
            fetchParentHierarchy(token).then((res) => {
                if (res) {
                    setParentSignageId(res.parentSignageId);
                    setParentSignageName(res.parentSignageName);
                }
            });
        }
    }, [isSignageTeammember, token]);

    useEffect(() => {
        const fetchProductionData = async () => {
            if (!signageId || !token) return;
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/signage/${signageId}/production_overview/datatable`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            page: pagination.pageIndex + 1,
                            limit: pagination.pageSize,
                            search: globalFilter,
                            sort: {
                                field: "created_at",
                                order: "desc",
                            },
                            filters: {
                                order_status: appliedFilters.orderStatus.value || undefined,
                                payment_status: "",
                                production_status: appliedFilters.productionStatus.value || undefined,
                            },
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch production data");
                }

                const data = await response.json();
                console.log("Fetched production overview:", data);
                const normalizedData = normalizeOrders(data?.data || []);
                setProducts(normalizedData);
                setTotalPages(data?.totalPages || 0);
                // setProducts(data?.data || []);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchProductionData();
    }, [token, signageId, pagination.pageIndex, pagination.pageSize, appliedFilters, globalFilter]);

    // Generate Excel export rows
    useEffect(() => {
        // Order status mapping function
        const getOrderStatusText = (status: number) => {
            switch (status) {
                case 100: return "Draft";
                case 101: return "Confirmed";
                case 200: return "Finalized";
                case 300: return "Scheduled";
                case 400: return "In Progress";
                case 500: return "Completed";
                case 600: return "Cancelled";
                default: return "";
            }
        };

        const rows: ProductionExportRow[] = products.map((product) => ({
            "Order ID": product.order_number || "",
            "Order Date": product.created_at
                ? new Date(product.created_at).toLocaleDateString('en-GB').replace(/\//g, '-')
                : "",
            "Product Name": product.product_name || "",
            "Customer": product.agent_name || "",
            "Office Name": product.office_name || "",
            "Status": getOrderStatusText(product.status || 0),
        }));

        setExportRows(rows);
    }, [products]);

    useEffect(() => {
        if (products != null && products?.length > 0) {
            const table = $("#transactions").DataTable({
                responsive: false,
                destroy: true,
                lengthChange: false,
                ordering: false,
            });

            return () => {
                table.destroy();
            };
        }
    }, [products]);


    const columns = useMemo(
        () => [
            // columnHelper.display({
            //     id: "sr_no",
            //     header: () => <div className="th-text">S.No.</div>,
            //     cell: ({ row, table }) => {
            //         const paginationState: any = (table.getState() as any).pagination || { pageIndex: 0, pageSize: 10 };
            //         const serialNumber = (paginationState.pageIndex || 0) * (paginationState.pageSize || 10) + row.index + 1;
            //         return <div className="text-sm ">{serialNumber}</div>;
            //     },
            // }),
            columnHelper.accessor("order_number", {
                id: "order_link",
                header: () => <div className="th-text S-No">Sales Order ID</div>,
                cell: (info) => {
                    const row = info.row.original;
                    // pick whichever your data uses:
                    const orderId =
                        (row as any).id ?? (row as any)._id ?? (row as any).order_id;

                    return (
                        <Link
                            to={`${baseRoute}/orderDetails/${orderId}`}
                            className="text-sm S-No order-link-1"
                        >
                            {info.getValue() ?? ""}
                        </Link>
                    );
                },
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),

            // columnHelper.accessor("order_number", {
            //     id: "order_link",
            //     header: () => <div className="th-text S-No">Sales Order ID</div>,
            //     cell: (info) => (
            //         // <a className="order-link" href="#">
            //         <div className="text-sm order-link-1 S-No">{info.getValue() ?? "---"}</div>
            //         // </a>
            //     ),
            // }),
            columnHelper.accessor("product_name", {
                header: () => <div className="th-text">Product Name</div>,
                cell: (info) => (
                    <div className="text-sm wrap-anywhere">{info.getValue() ?? "---"}</div>
                ),
            }),
            columnHelper.accessor("created_at", {
                header: () => <div className="th-text">Order Date</div>,
                cell: (info) => (
                    <div className="text-sm ">
                        {info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "N/A"}
                    </div>
                ),
            }),
            columnHelper.accessor("email", {
                header: () => <div className="th-text">Email</div>,
                cell: (info) => (
                    <div className="text-sm ">{info.getValue() ?? "---"}</div>
                ),
            }),
            columnHelper.accessor("brokerage_firm_name", {
                header: () => <div className="th-text">Brokerage Firm</div>,
                cell: (info) => (
                    <div className="text-sm ">{info.getValue() ?? "---"}</div>
                ),
            }),
            // columnHelper.accessor((row) => row.office, {
            //     id: "office",
            //     header: () => <div className="th-text">Office</div>,
            //     cell: (info) => (
            //         <div className="text-sm ">{info.getValue() ?? "---"}</div>
            //     ),
            // }),
            columnHelper.accessor(
                (row) => {
                    const office = row?.office_name || row?.office || "";
                    return office.trim() || "---";
                },
                {
                    id: "office_name",
                    header: () => <div className="th-text">Office</div>,
                    cell: (info) => {
                        const val = info.getValue<string>();
                        const isMissing = val === "---";
                        return (
                            <div className={`text-sm ${isMissing ? "text-muted" : ""}`}>
                                {val}
                            </div>
                        );
                    },
                    filterFn: fuzzyTextFilter,
                    enableGlobalFilter: true,
                    sortingFn: "alphanumeric",
                }
            ),
            columnHelper.accessor(
                (row) => {
                    const agent = row?.agent_name || row?.agent || "";
                    return agent.trim() || "---";
                },
                {
                    id: "agent_name",
                    header: () => <div className="th-text">Agent</div>,
                    cell: (info) => {
                        const val = info.getValue<string>();
                        const isMissing = val === "---";
                        return (
                            <div className={`text-sm ${isMissing ? "text-muted" : ""}`}>
                                {val}
                            </div>
                        );
                    },
                    filterFn: fuzzyTextFilter,
                    enableGlobalFilter: true,
                    sortingFn: "alphanumeric",
                }
            ),
            columnHelper.accessor("status", {
                header: () => <div className="th-text">Status</div>,
                cell: (info) => {
                    const orderStatus = Number(info.row.original?.status ?? 0);
                    // console.log("orderStatus", info.row.original);    
                    return (
                        <span>
                            <SalesOrderStatusBadge type="order" value={orderStatus} />
                        </span>
                    );
                },
            }),

            //             columnHelper.accessor("status", {
            //   header: () => <div className="th-text text-center">Status</div>,
            //   cell: (info) => {
            //     const raw = info.getValue() as unknown;
            //     const orderStatus =
            //       typeof raw === "number" ? raw : (parseInt(String(raw), 10) || 0);

            //     return (
            //       <div className="text-sm d-flex justify-content-center">
            //         <SalesOrderStatusBadge type="order" value={orderStatus} />
            //       </div>
            //     );
            //   },
            // });

            // columnHelper.accessor("order_status", {
            //     header: () => <div className="th-text">Status</div>,
            //     cell: (info) => {
            //         const status = info.getValue();
            //         const display =
            //             status === 100
            //                 ? "Draft"
            //                 : status === 200
            //                     ? "Finalized"
            //                     : status === 300
            //                         ? "Scheduled"
            //                         : status === 400
            //                             ? "In Progress"
            //                             : status === 500
            //                                 ? "Completed"
            //                                 : "Cancelled";
            //         return (
            //             <div className="text-sm ">
            //                 <span className="status-badge status-pending">{display}</span>
            //             </div>
            //         );
            //     },
            // }),
            columnHelper.display({
                id: "production_status",
                header: () => (
                    <div className="th-text ">Production Status</div>
                ),
                cell: ({ row, table }) => {
                    const m = table.options.meta as any;
                    const statusKey = `${row.original.order_id}-${row.original.product_id}-${row.original.variant_id}`;
                    const statusValue = row.original.production_status || "not_started";
                    const selectedOption =
                        productionStatusOptions.find((opt) => opt.value === (m.productionStatusMap[statusKey] ?? statusValue)) ||
                        productionStatusOptions[0];

                    return (
                        <div>
                            <select
                                className="form-select form-select-sm w-75"
                                value={m.productionStatusMap[statusKey] ?? statusValue}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    if (!newValue) return;
                                    m.setProductionStatusMap((prev: any) => ({
                                        ...prev,
                                        [statusKey]: newValue,
                                    }));
                                }}
                                style={{ minHeight: "32px", height: "32px", fontSize: "14px" }}
                            >
                                {productionStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: "actions",
                header: () => <div className="th-text">Actions</div>,
                cell: ({ row, table }) => {
                    const m = (table.options.meta as any);
                    const statusKey = `${row.original.order_id}-${row.original.product_id}-${row.original.variant_id}`;
                    const statusValue = row.original.production_status || "not_started";
                    const selectedStatus = m.productionStatusMap[statusKey] || statusValue;
                    //    const row1 = m.row.original;
                    // pick whichever your data uses:
                    const orderId =
                        (row as any).id ?? (row as any)._id ?? (row as any).order_id;
                    return (
                        <div className="d-flex  align-items-center gap-2">
                            <UpdateIconButton
                                className="btn action-btn fs-2 text-center"
                                title="Update"
                                onClick={() => {
                                    if (!row.original.product_id || !selectedStatus) {
                                        showError("Please select a status before saving.");
                                        return;
                                    }
                                    m.updateProductionStatus(
                                        row.original.order_id,
                                        row.original.variant_id,
                                        row.original.product_id,
                                        selectedStatus
                                    );
                                }}
                            />
                            <ViewIconButton
                                className="btn action-btn fs-2 "
                                to={`${baseRoute}/orderDetails/${row.original.order_id}`}
                                state={{ type: "View" }}
                                title="View"
                            />
                        </div>
                    );
                },
            }),
        ],
        []
    );

    const table = useReactTable<any>({
        data: products,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: { pagination },
        onPaginationChange: setPagination,
        manualPagination: true,
        pageCount: totalPages,
        meta: {
            productionStatusMap,
            setProductionStatusMap,
            updateProductionStatus, // This is your async API call!
        },
    });
    const pageCount = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex;



    return (
        <div className="content">
            <div className="po-header mb-4">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2">
                        <h4 className="mb-1 po-title">Production Overview</h4>
                        <div className="po-subtitle">Manage and track production status of orders</div>
                    </div>

                    {/* tools block (search + buttons) */}
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {/* Search */}
                        <div className="input-group po-search">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                ref={searchInputRef}
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder="Search orders, products, or agents..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setGlobalFilter(searchInput);
                                        setPagination((prev) => ({ ...prev, pageIndex: 0 })); // :white_check_mark: Reset to page 1
                                    }
                                }}
                                autoComplete="off"
                            />
                        </div>

                        {/* Filters toggle */}
                        <button
                            type="button"
                            className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                            onClick={() => setFiltersOpen(v => !v)}
                            aria-expanded={filtersOpen}
                            aria-controls="production-filters"
                        >
                            <i className="ri-filter-3-line me-2" />
                            <span>Filters</span>
                            <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>

                        {/* Excel Export */}
                        <ExportAsExcel
                            data={exportRows}
                            headers={[
                                "Order ID",
                                "Order Date",
                                "Product Name",
                                "Customer",
                                "Office Name",
                                "Status",
                            ]}
                            fileName="Production_Overview"
                        >
                            {(props) => (
                                <button {...props} className="export-button">
                                    <i className="ri-file-excel-2-line me-2" />
                                    Export to Excel
                                </button>
                            )}
                        </ExportAsExcel>
                    </div>
                </div>
                <div id="production-filters" className={`overflow-visible po-filters ${filtersOpen ? "show" : ""}`}>
                    <div className="po-filters-bar d-flex">
                        <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">

                            {/* Order Status */}
                            {/* <div className="col-12 col-lg-auto">
                                <label className="form-label mb-1 label-1">Order Status</label>
                                <div className="po-rs min-220">
                                    <Select
                                        classNamePrefix="rs"
                                        options={ORDER_STATUS_OPTIONS}
                                        value={orderStatusFilter}
                                        onChange={(option) => option && setOrderStatusFilter(option)}
                                        isSearchable
                                        styles={{
                                            control: (b) => ({ ...b, minHeight: 40, borderRadius: 10 }),
                                            menu: (b) => ({ ...b, zIndex: 9999 }),
                                        }}
                                    />
                                </div>
                            </div> */}

                            {/* Production Status */}
                            <div className="col-12 col-lg-auto">
                                <label className="form-label mb-1 label-1">Production Status</label>
                                <div className="po-rs min-220">
                                    <Select
                                        classNamePrefix="rs"
                                        options={PRODUCTION_STATUS_OPTIONS}
                                        value={productionStatusFilter}
                                        onChange={(option) => option && setProductionStatusFilter(option)}
                                        isSearchable
                                        components={{ DropdownIndicator }}
                                        styles={customSelectStyles}
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                                <button className="btn btn-primary table-addbutton flex-fill flex-lg-grow-0" onClick={() => {
                                    // Apply the current filter selections to trigger API call
                                    setAppliedFilters({
                                        orderStatus: orderStatusFilter,
                                        productionStatus: productionStatusFilter
                                    });
                                    setPagination({ ...pagination, pageIndex: 0 });
                                }}>
                                    Apply Filters
                                </button>

                                <button
                                    className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-lg-grow-0"
                                    onClick={() => {
                                        // Reset all filters to default values
                                        setOrderStatusFilter(ORDER_STATUS_OPTIONS[0]);
                                        setProductionStatusFilter(PRODUCTION_STATUS_OPTIONS[0]);
                                        setAppliedFilters({
                                            orderStatus: ORDER_STATUS_OPTIONS[0],
                                            productionStatus: PRODUCTION_STATUS_OPTIONS[0]
                                        });
                                        setGlobalFilter(""); // Clear search
                                        setSearchInput(""); // Clear search input
                                        setPagination({ pageIndex: 0, pageSize: 10 });
                                    }}
                                >
                                    Clear
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                {/* Header like screenshot (left title, right count pill) */}
                <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
                    <h5 className="mb-0 ">Production Orders</h5>
                    <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                        {products.length} orders
                    </span>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />
                {/* DESKTOP (≥ 1022px) */}
                <div className="rdt-desktop">
                    <div
                        className="production-scroll"
                        style={{ "--rdt-min": "1200px" } as React.CSSProperties}
                    >
                        <table className="table production-table align-middle mb-0 table-1">
                            {loading ? (
                                <DesktopTableLoader table={table} message="Loading production orders…" />
                            ) : (
                                <DesktopTableParts table={table} />
                            )}
                        </table>
                    </div>
                </div>

                {/* MOBILE (≤ 1021px) */}
                <div className="rdt-mobile">
                    {loading ? (
                        <MobileCardsLoader message="Loading production orders…" />
                    ) : (
                        <MobileCardsOnly
                        table={table}
                        idColId="order_link"
                        titleColId="product_name"
                        actionColId="actions"
                    />
                    )}
                </div>

            </div>

            <DataTableStatus
                loading={loading}
                isEmpty={table.getRowModel().rows.length === 0}
            >
                <div className="mt-4 mb-4">
                    <TablePaginationControls table={table} />
                </div>
            </DataTableStatus>

        </div>
    );
};

export default SignageProductionOverview;
